import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteProvider, updateProviderReference, userBaseURL } from "api";
import { useGetProviderDetails } from "api/queries/user/useGetProviderDetails";
import Button from "components/atoms/Button/Button";
import ProviderLogo from "components/atoms/ProviderLogo/ProviderLogo";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Input from "components/v2/Input/Input";
import Dialog from "components/organisms/Dialog/Dialog";
import Alert from "components/molecules/Alert/Alert";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import "./providerDialog.scss";

interface ProviderDialogProps {
  isOpen: boolean;
  close: () => void;
  provider: any;
  locationReferenceNumber: string;
  refetch: () => void;
}

function ProviderDialog({ isOpen, close, provider, locationReferenceNumber, refetch }: ProviderDialogProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const viewOnly = Boolean(!adminSettings?.edit_providers);

  const {
    data: details,
    isLoading: isLoadingProviderDetails,
    refetch: refetchProviderDetails,
  }: any = useGetProviderDetails(locationReferenceNumber, provider.slug, {
    enabled: isOpen && Boolean(provider.slug),
  });

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [providerReference, setProviderReference] = useState("");

  useEffect(() => {
    if (isOpen) {
      setProviderReference(details?.provider_reference ?? "");
    }
  }, [isOpen, provider.slug, details?.provider_reference]);

  const loadedReference = details?.provider_reference ?? "";
  const hasReferenceChanged = providerReference !== loadedReference;

  const { mutate: saveProviderReference, isLoading: isSaving } = useMutation({
    mutationFn: () =>
      updateProviderReference(locationReferenceNumber, provider.slug, {
        provider_reference: providerReference,
      }),
    onSuccess: () => {
      toast(
        intl.formatMessage({
          id: "settings.provider.providerReferenceUpdated",
          defaultMessage: "Provider reference updated successfully.",
        }),
        { type: "success" },
      );
      queryClient.invalidateQueries({ queryKey: ["draft-provider-options"] });
      refetchProviderDetails();
      refetch();
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const { mutate: deleteProviderMutation, isLoading: isDeleting } = useMutation({
    mutationFn: () => deleteProvider(locationReferenceNumber, provider.slug),
    onSuccess: (res) => {
      setShowConfirmDelete(false);
      toast(res.data.detail, { type: "success" });
      refetch();
      close();
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const handleDeleteProvider = () => {
    deleteProviderMutation();
  };

  const handleExportReviews = () => {
    window.open(`${userBaseURL}/admin/settings/providers/export-reviews/${locationReferenceNumber}/${provider.slug}`);
  };

  function formatNumber(value: any) {
    return isNaN(value) ? "n/a" : Math.round(value * 100) / 100;
  }

  if (Object.keys(provider).length === 0) return null;

  return (
    <Dialog
      size="md"
      show={isOpen}
      title={
        <div className="d-flex align-items-center">
          <ProviderLogo name={provider.name} logo={provider.logo} />
          <div className="ml-3">{provider.name}</div>
        </div>
      }
      onHide={close}
    >
      {isLoadingProviderDetails || !details ? (
        <LoadingSkeleton rows={3} />
      ) : (
        <>
          <div className="font-b3" style={{ borderBottom: "1px solid #f1f3f4" }}>
            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="settings.provider.contactPerson" defaultMessage="Contact person" />
              </Col>
              <Col>{details.contact_person}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="general.email" defaultMessage="Email" />
              </Col>
              <Col>{details.email}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="settings.provider.providerReference" defaultMessage="Provider reference" />
              </Col>
              <Col>
                <Input
                  value={providerReference}
                  onChange={(event) => setProviderReference(event.target.value)}
                  disabled={viewOnly || isSaving}
                  saveOnBlur={false}
                  width="100%"
                  testId="settings-provider-reference-input"
                />
              </Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="settings.provider.availableInLocations" defaultMessage="Available in locations" />
              </Col>
              <Col className="mb-3">
                {details.available_locations.length > 0
                  ? details.available_locations.map((locationRef: any) => (
                      <p className="p-0 m-0" key={locationRef}>
                        {locationRef}
                      </p>
                    ))
                  : "-"}
              </Col>
            </Row>
          </div>

          <div className="mt-3 font-b3">
            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="settings.provider.bookedThisMonth" defaultMessage="Booked this month" />
              </Col>
              <Col>{details.booked_this_month}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="settings.provider.bookedTotal" defaultMessage="Booked total" />
              </Col>
              <Col>{details.booked_total}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="general.reviews" defaultMessage="Reviews" />
              </Col>
              <Col>{details.reviews}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="settings.provider.onTimePerformance" defaultMessage="On time performance" />
              </Col>
              <Col>{formatNumber(details.on_time_performance)}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="general.communication" defaultMessage="Communication" />
              </Col>
              <Col>{formatNumber(details.communication)}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="general.pricing" defaultMessage="Pricing" />
              </Col>
              <Col>{formatNumber(details.pricing)}</Col>
            </Row>

            <Row className="provider-info-row">
              <Col className="text-secondary">
                <FormattedMessage id="general.overallPerformance" defaultMessage="Overall performance" />
              </Col>
              <Col>{formatNumber(details.overall_performance)}</Col>
            </Row>
          </div>
          <Alert variant="success" className="mt-3">
            <FormattedMessage
              id="settings.provider.performanceDetailsInfo"
              defaultMessage="Performance details are across all locations."
            />
          </Alert>
          <div className="d-flex justify-content-between" style={{ marginTop: "40px" }}>
            {!showConfirmDelete && (
              <>
                {!viewOnly && (
                  <Button size="lg" variant="danger" onClick={() => setShowConfirmDelete(true)}>
                    <FormattedMessage id="settings.provider.removeProvider" defaultMessage="Remove provider" />
                  </Button>
                )}
                <div className="d-flex">
                  {!viewOnly && hasReferenceChanged && (
                    <Button
                      size="lg"
                      variant="primary"
                      className="mr-2"
                      isLoading={isSaving}
                      onClick={() => saveProviderReference()}
                    >
                      <FormattedMessage id="general.save" defaultMessage="Save" />
                    </Button>
                  )}
                  <Button size="lg" variant="outline-primary" onClick={() => handleExportReviews()}>
                    <FormattedMessage id="components.molecules.navigation.export" defaultMessage="Export" />
                  </Button>
                </div>
              </>
            )}
            {showConfirmDelete && (
              <div className="w-100">
                <div style={{ maxWidth: "450px" }}>
                  <Alert variant="warning" className="mb-3">
                    <FormattedMessage
                      id="settings.provider.confirmDelete"
                      defaultMessage="Are you sure you want to remove {provider} from this account? <br /> By removing this provider, you will no longer be able to invite them for new shipments with immediate effect."
                      values={{ provider: provider.name, br: <br /> }}
                    />
                  </Alert>
                </div>
                <div className="d-flex justify-content-between">
                  <Button size="lg" variant="outline-primary" onClick={() => setShowConfirmDelete(false)}>
                    <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button size="lg" variant="danger" isLoading={isDeleting} onClick={() => handleDeleteProvider()}>
                    <FormattedMessage id="settings.provider.removeProvider" defaultMessage="Remove provider" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
}

export default ProviderDialog;
